/**
 * LatestNewsSection displays tabs for group and hotel news, and handles tab switching logic.
 * Manages news data and tab state for latest news display.
 */
"use client";
import placeHolderImage from "@/assets/images/Info-img.jpg";
import { Button } from "@/components/atoms/button";

import { LinkButton } from "@/components/atoms/link-button";
import useJapanAreasStore from "@/store/hooks/useGlobal";
import { NewsYearMonthDayItem, UploadFile } from "@/types/generated";
import {
  cleanStylesFromContent,
  getNewDetailPreviewImage,
  getNewsRelatedHotelId,
  kebabCase,
} from "@/utils";
import { CircleChevronRight } from "lucide-react";
import React, { useMemo, useState } from "react";
import NewsCardItem from "../../news/NewCardItem";
import {
  cn,
  generateLinkFromGeneralLink,
  generateTargetFromLink,
} from "@/lib/utils";

interface LabelLatestNews {
  groupNewsBtn: string;
  hotelNewsBtn: string;
  seeMoreBtn: string;
  title: string;
}

interface ILatestNewsSectionProps {
  label: LabelLatestNews;
  groupNewsData: NewsYearMonthDayItem[];
  hotelNewsData: NewsYearMonthDayItem[];
  locale: string;
  showHotelName?: boolean;
}

enum TAB {
  GROUP,
  HOTEL,
}

const LatestNewsSection: React.FC<ILatestNewsSectionProps> = ({
  label,
  groupNewsData,
  hotelNewsData,
  locale,
  showHotelName = false,
}) => {
  const { japanAreas } = useJapanAreasStore();
  const listHotel = useMemo(() => {
    return japanAreas?.flatMap((area) => area.hotels) || [];
  }, [japanAreas]);

  /**
   * Determines the default active tab based on available news data.
   */
  let defaultTab: TAB;

  if (groupNewsData.length > 0 && hotelNewsData?.length > 0) {
    defaultTab = TAB.GROUP;
  } else if (groupNewsData.length > 0) {
    defaultTab = TAB.GROUP;
  } else {
    defaultTab = TAB.HOTEL;
  }

  const [activeTab, setActiveTab] = useState<TAB>(defaultTab);
  /**
   * Selects current news data based on active tab (group or hotel).
   */
  const currentNewsData =
    activeTab === TAB.GROUP ? groupNewsData : hotelNewsData;
  /**
   * Maps and transforms news data for display, including image selection and link generation.
   */
  const mappedNewsData = useMemo(() => {
    if (!currentNewsData?.length) return [];
    return currentNewsData
      .filter(
        (item): item is NewsYearMonthDayItem =>
          item !== null && item !== undefined,
      )
      .map((item) => {
        // Get related hotel ID for news item
        const relatedHotelId = getNewsRelatedHotelId(item);
        // Get preview image for hotel news
        const hotelNewsImage = getNewDetailPreviewImage(
          relatedHotelId,
          listHotel,
        );
        // Select feature image if flagged, otherwise use hotel image
        const featureImage = item?.properties?.featured_image;
        const imageUrl = item?.properties?.use_featured_Image
          ? featureImage || hotelNewsImage
          : hotelNewsImage;
        return {
          title: showHotelName && item?.properties?.hotel_names ? `${item?.properties?.hotel_names} ${item?.properties?.header_title}` : item?.properties?.header_title,
          description: cleanStylesFromContent(item?.properties?.content || ""),
          imageUrl: imageUrl || placeHolderImage,
          date: item?.properties?.date_created || "",
          // Generate external or internal news link
          url: item?.properties?.external_link
            ? generateLinkFromGeneralLink(
                item?.properties?.external_link,
                locale,
              )
            : `/${locale}/news-${kebabCase(item?.item_name || "")}`,
          // Set link target for external or internal
          target: item?.properties?.external_link
            ? generateTargetFromLink(item?.properties?.external_link)
            : "_blank",
          relatedHotelId,
          imageAlt: item?.properties?.featured_image?.alternativeText || "",
          use_featured_Image: item?.properties?.use_featured_Image,
        };
      });
  }, [currentNewsData, listHotel, locale, showHotelName]);

  return (
    <div className="mx-auto h-max w-full px-4 py-10 md:mx-4 md:max-w-[1132px]">
      <div className="flex w-full flex-col items-center">
        <h2 className="pb-6 text-2xl leading-9 font-[var(--font-semibold)] text-[var(--fill-base-main)] lg:pb-10 lg:text-4xl lg:leading-10.5">
          {label.title ?? "最新ニュース"}
        </h2>
        {/* Tab buttons for switching between group and hotel news */}
        <div
          className={cn("flex rounded-full shadow-sm sm:w-[unset]", {
            "w-full": hotelNewsData?.length > 0 && groupNewsData?.length > 0,
          })}
        >
          {groupNewsData.length > 0 && (
            <Button
              variant={activeTab === TAB.GROUP ? "primary" : null}
              className={cn(
                "h-10 rounded-full font-[var(--font-semibold)] sm:w-[unset] lg:h-9 lg:min-w-[179px]",
                {
                  "w-[50%]":
                    hotelNewsData?.length > 0 && groupNewsData?.length > 0,
                },
              )}
              onClick={() => setActiveTab(TAB.GROUP)}
            >
              {label.groupNewsBtn ?? "グループニュース"}
            </Button>
          )}
          {hotelNewsData?.length > 0 && (
            <Button
              variant={activeTab === TAB.HOTEL ? "primary" : null}
              className={cn(
                "h-10 rounded-full font-[var(--font-semibold)] sm:w-[unset] lg:h-9 lg:min-w-[179px]",
                {
                  "w-[50%]":
                    hotelNewsData?.length > 0 && groupNewsData?.length > 0,
                },
              )}
              onClick={() => setActiveTab(TAB.HOTEL)}
            >
              {label.hotelNewsBtn ?? "ホテルニュース"}
            </Button>
          )}
        </div>
        {/* News card grid for displaying mapped news data */}
        <div className="grid grid-cols-1 justify-items-center gap-3 py-6 md:py-8 lg:grid-cols-2 lg:gap-6">
          {mappedNewsData.map((item, index) => {
            const mappedNewsDataKey = `mapped-news-data-${index}`;
            return (
              <NewsCardItem
                key={mappedNewsDataKey}
                title={item.title || ""}
                relatedHotelId={item.relatedHotelId || ""}
                featureImage={item.imageUrl as UploadFile}
                date={item.date}
                url={item.url}
                target={item.target}
                imageAlt={item.imageAlt}
                useFeaturedImage={item.use_featured_Image}
              />
            );
          })}
        </div>
        {/* See more button for navigating to news page */}
        <LinkButton
          href="/news"
          className="text-md w-full cursor-pointer !justify-between rounded-sm py-6 leading-6 font-[var(--font-medium)] md:justify-center lg:h-[55px] lg:w-64"
        >
          {label.seeMoreBtn ?? "もっと見る"}
          <CircleChevronRight strokeWidth="1px" className="ml-10 h-4.5 w-4.5" />
        </LinkButton>
      </div>
    </div>
  );
};

export default LatestNewsSection;
